import { type JSX, useId, type ComponentProps, type ReactNode } from 'react'

import { useReadOnly } from '../components/read-only.tsx'
import { Input as UiInput } from '../components/ui/input.tsx'
import { Label } from '../components/ui/label.tsx'
import { cn } from '../lib/cn.ts'

export type InputProps = ComponentProps<typeof UiInput> & {
  'data-testid': string
  label: ReactNode
  /** A message means invalid; it also sets aria-invalid. */
  error?: string
  hint?: ReactNode
}

/**
 * Input — a labelled field with one message slot.
 *
 * The id comes from `useId`, not from the label text: two fields sharing a
 * label would otherwise collide and every label would point at the first
 * field. Error and hint share a slot and the error wins, because two messages
 * under one field is how a user misses the one that matters.
 *
 * @param props - Input props.
 * @param props.label - Field label, rendered above the control.
 * @param props.error - Error message; sets aria-invalid.
 * @param props.hint - Shown when there is no error.
 * @param props.id - Overrides the generated id.
 * @returns The labelled field, or nothing on a read-only screen.
 */
export function Input({
  label,
  error,
  hint,
  id,
  className,
  ...props
}: InputProps): JSX.Element | null {
  const readOnly = useReadOnly()
  const generated = useId()
  if (readOnly) return null
  const fieldId = id ?? generated
  const messageId = `${fieldId}-message`
  const message = error ?? hint

  return (
    <div className="grid gap-2">
      <Label htmlFor={fieldId} className="[&&]:type-eyebrow">
        {label}
      </Label>
      <UiInput
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
        className={cn(
          'h-9 rounded-none border-(length:--hairline-width) border-input bg-card dark:bg-card shadow-none focus-visible:ring-0 focus-visible:border-input disabled:bg-sunk dark:disabled:bg-sunk disabled:text-muted-foreground disabled:opacity-100',
          error && 'border-destructive focus-visible:border-destructive',
          className
        )}
        {...props}
      />
      {message ? (
        <p
          id={messageId}
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}
